import { Component, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { PushNotificationsService } from '@core/services/push-notifications/push-notifications.service';
import { SocketService } from '@core/services/socket.service';
import { getErrorCode } from '@core/utils/get-error-code';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { PasswordValidation } from '@validators/password-validation';
import { sentry } from 'sentry';
import { slideInRight } from '../../animations/slide-in-right.animation';

type LoginStep = 'email' | 'password' | 'softwareToken' | 'recoveryCode' | 'resetPassword' | 'successOnResetPassword';

@Component({
  selector: 'login-reset-password',
  templateUrl: './reset-password.component.html',
  encapsulation: ViewEncapsulation.None,
  animations: [slideInRight],
})
export class LoginResetPasswordComponent {
  public verificationCodeForm: FormArray;
  public loginStep: LoginStep;
  public resetPasswordForm: FormGroup;
  public step: 'password' | 'success';
  public isLoading: boolean;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationService: NavigationService,
    private readonly pushNotificationsService: PushNotificationsService,
    private readonly customMessageService: CustomMessagesService,
    private readonly socketService: SocketService,
    private readonly fuseProgressBar: FuseProgressBarService,
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router
  ) {
    this.step = 'password';
    this.isLoading = false;
    const email = this.activatedRoute.snapshot.params.email;

    this.verificationCodeForm = this.formBuilder.array([
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required])
    ]);

    this.resetPasswordForm = this.formBuilder.group({
      email: [email, [Validators.required, Validators.email, Validators.pattern(/@.+\./)]],
      password: [null, [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(new RegExp('[0-9]{1,}')),
        Validators.pattern(new RegExp('[a-z]{1,}')),
        Validators.pattern(new RegExp('[A-Z]{1,}')),
        Validators.pattern(/\^|\$|\*|\.|\[|\]|\{|\}|\(|\)|\?|\-|\"|\!|\@|\#|\%|\&|\/|\\|\,|\>|\<|\'|\:|\;|\||\_|\~|\`|\+|\='/)
      ]],
      confirmPassword: [null, Validators.required]
    }, {
      validator: PasswordValidation.validate.bind(this)
    });
  }

  public async resetPassword(): Promise<void> {
    this.fuseProgressBar.show();
    this.isLoading = true;

    const input = {
      email: this.activatedRoute.snapshot.params.email,
      newPassword: this.resetPasswordForm.value.password,
      verificationCode: this.verificationCodeForm.value.join('')
    };

    try {
      await this.authenticationService.confirmCodeAndCreateNewPassword(input);
      this.step = 'success';
      await this.auth();
    } catch (err) {
      const code = getErrorCode(err);
      if (['ExpiredCodeException', 'CodeMismatchException'].includes(code)) {
        this.verificationCodeForm.setErrors({ invalid: true });
        return;
      }

      if (code === 'InvalidParameterException') {
        this.resetPasswordForm.get('password').setErrors({ pattern: true });
        return;
      }

      if (code === 'LimitExceededException') {
        this.resetPasswordForm.setErrors({ limitExceeded: true });
        return;
      }

      this.resetPasswordForm.setErrors({ unknown: true });
    } finally {
      this.isLoading = false;
      this.fuseProgressBar.hide();
    }
  }

  public async auth(): Promise<void> {
    try {
      this.isLoading = true;
      this.fuseProgressBar.show();

      const { email, password } = this.resetPasswordForm.getRawValue();
      this.resetPasswordForm.get('password').markAsTouched();

      await this.authenticationService.login({ provider: 'email-password', email, password });
      await this.refreshItemsAndConnectSocket();
    } catch (err) {
      const code = getErrorCode(err);

      if (code === 'NewPasswordRequired') {
        this.toNewPasswordStep();
        return;
      }

      if (code === 'SoftwareTokenMfaRequired') {
        this.toSoftwareTokenStep();
        return;
      }

      if (this.resetPasswordForm.get('password').valid) {
        this.resetPasswordForm.get('password').setErrors({ unknown: true });
      }

      sentry.captureException(err, {
        user: {
          email: this.resetPasswordForm.get('email').value
        },
        extra: {
          message: 'Error on authentication',
          err: JSON.stringify(err)
        }
      });

    } finally {
      this.isLoading = false;
      this.fuseProgressBar.hide();
    }
  }

  public async authWithSoftwareToken(): Promise<void> {
    try {
      this.isLoading = true;
      this.fuseProgressBar.show();

      await this.authenticationService.loginWithSoftwareToken({
        email: this.resetPasswordForm.get('email').value,
        code: this.verificationCodeForm.value.join('')
      });

      await this.refreshItemsAndConnectSocket();
    } catch (err) {
      const code = getErrorCode(err);
      if (code === 'CodeMismatchException') {
        this.verificationCodeForm.setErrors({ incorrect: true });
        return;
      }

      if (code === 'NotAuthorizedException') {
        this.resetPasswordForm.reset();
        this.loginStep = 'email';
        this.customMessageService.error('LOGIN.AUTH.MFA.EXPIRED_CODE');
        this.verificationCodeForm.reset();
        return;
      }

      if (code === 'UnauthorizedUser') {
        this.resetPasswordForm.reset();
        this.loginStep = 'email';
        this.customMessageService.error('LOGIN.AUTH.ERRORS.UNAUTHORIZED');
        this.verificationCodeForm.reset();
        return;
      }

      this.customMessageService.error('LOGIN.AUTH.ERRORS.UNKNOWN');
      console.error(err);

      sentry.captureException(err, {
        user: {
          email: this.resetPasswordForm.get('email').value
        },
        extra: {
          message: 'Error on authentication with verification code',
          err: JSON.stringify(err)
        }
      });
    } finally {
      this.isLoading = false;
      this.fuseProgressBar.hide();
    }
  }

  private async refreshItemsAndConnectSocket(): Promise<void> {
    const firstVisibleItem = this.navigationService.getFirstVisibleItem(true);
    const redirectToDestinationUrl = localStorage.getItem('REDIRECT_TO_URL_AFTER_LOGIN');
    location.href = redirectToDestinationUrl || firstVisibleItem.url;
    if (redirectToDestinationUrl) {
      localStorage.removeItem('REDIRECT_TO_URL_AFTER_LOGIN');
    }

    await this.pushNotificationsService.refreshNotificationSubscription();
    await this.socketService.reconnect();
  }

  private toNewPasswordStep(): void {
    this.resetPasswordForm = this.formBuilder.group({
      password: [null, [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(new RegExp('[0-9]{1,}')),
        Validators.pattern(new RegExp('[a-z]{1,}')),
        Validators.pattern(new RegExp('[A-Z]{1,}')),
        Validators.pattern(/\^|\$|\*|\.|\[|\]|\{|\}|\(|\)|\?|\-|\"|\!|\@|\#|\%|\&|\/|\\|\,|\>|\<|\'|\:|\;|\||\_|\~|\`|\+|\='/)
      ]],
      confirmPassword: [null, Validators.required]
    }, {
      validator: PasswordValidation.validate.bind(this)
    });

    this.loginStep = 'resetPassword';
  }

  public toSoftwareTokenStep(): void {
    this.verificationCodeForm = this.formBuilder.array([
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required])
    ]);

    this.loginStep = 'softwareToken';
  }
}
